import { useCallback, useEffect, useRef, useState } from "react";

import {
  getSubscriptionApiError,
  getSubscriptionDashboard,
} from "../../../../../services/subscriptionServices";

import { normalizeSubscriptionDashboard } from "../utils/subscriptionNormalizers";

const createEmptyDashboard = ({
  months = 12,
  renewalDays = 30,
  product = "all",
} = {}) =>
  normalizeSubscriptionDashboard({
    filters: {
      months,
      renewal_days: renewalDays,
      history_product: product,
    },
  });

export default function useSubscriptionDashboard({
  initialMonths = 12,
  initialRenewalDays = 30,
} = {}) {
  const requestIdRef = useRef(0);
  const hasLoadedRef = useRef(false);

  const [months, setMonthsState] = useState(initialMonths);

  const [renewalDays, setRenewalDaysState] = useState(initialRenewalDays);

  const [product, setProductState] = useState("all");

  const [dashboard, setDashboard] = useState(() =>
    createEmptyDashboard({
      months: initialMonths,
      renewalDays: initialRenewalDays,
      product: "all",
    }),
  );

  const [isLoading, setIsLoading] = useState(true);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const [hasLoaded, setHasLoaded] = useState(false);

  const [error, setError] = useState("");

  const loadDashboard = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = requestIdRef.current + 1;

      requestIdRef.current = requestId;

      if (silent) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      setError("");

      try {
        const response = await getSubscriptionDashboard({
          months,
          renewalDays,
          product,
        });

        if (requestId !== requestIdRef.current) {
          return;
        }

        const normalizedDashboard = normalizeSubscriptionDashboard(response);

        setDashboard(normalizedDashboard);

        /*
         * Si le serveur ne reconnaît plus le
         * produit, il renvoie automatiquement all.
         */
        if (normalizedDashboard.filters.product !== product) {
          setProductState(normalizedDashboard.filters.product);
        }

        hasLoadedRef.current = true;
        setHasLoaded(true);
      } catch (loadError) {
        if (requestId !== requestIdRef.current) {
          return;
        }

        setError(
          getSubscriptionApiError(
            loadError,
            "Impossible de charger le tableau de pilotage.",
          ),
        );
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [months, renewalDays, product],
  );

  useEffect(() => {
    /*
     * Au premier chargement, on affiche le
     * skeleton complet.
     *
     * Pour un changement de produit ou de période,
     * seuls le graphique et l’icône d’actualisation
     * passent en chargement.
     */
    loadDashboard({
      silent: hasLoadedRef.current,
    });

    return () => {
      requestIdRef.current += 1;
    };
  }, [loadDashboard]);

  const setMonths = useCallback((value) => {
    const nextValue = Number(value);

    if ([6, 12, 24].includes(nextValue)) {
      setMonthsState(nextValue);
    }
  }, []);

  const setRenewalDays = useCallback((value) => {
    const nextValue = Number(value);

    if (!Number.isFinite(nextValue)) {
      return;
    }

    setRenewalDaysState(Math.min(Math.max(nextValue, 1), 180));
  }, []);

  const setProduct = useCallback((value) => {
    const nextProduct = String(value || "all").trim();

    setProductState(nextProduct || "all");
  }, []);

  return {
    dashboard,

    summary: dashboard.summary,

    summaryDetails: dashboard.summaryDetails,

    history: dashboard.history,

    actions: dashboard.actions,

    priorities: dashboard.priorities || [],

    renewalPipeline: dashboard.renewalPipeline || {
      anticipate: [],
      toPrepare: [],
      quoteSent: [],
      followUp: [],
      accepted: [],
      renewed: [],
    },

    renewalTimeline: dashboard.renewalTimeline || [],

    productPortfolio: dashboard.productPortfolio || [],

    forecast: dashboard.forecast || {
      assumption: "",
      monthsCount: 12,
      totalPotentialRevenue: 0,
      totalAcceptedRevenue: 0,
      months: [],
    },

    availableProducts: dashboard.availableProducts || [],

    months,
    setMonths,

    product,
    setProduct,

    renewalDays,
    setRenewalDays,

    isLoading,
    isRefreshing,

    isHistoryLoading: isLoading || isRefreshing,

    hasLoaded,
    error,

    reloadDashboard: loadDashboard,
  };
}
